import { type ReactNode, useMemo } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Stack,
  Step,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@schaeffler-commerce/design-system";
import { FormProvider } from "react-hook-form";
import { createOnboardingSteps, type OnboardingFormData } from "../../api/getOnboardingInitialData";
import { useTranslation } from "react-i18next";
import ExpiredLinkPage from "~/pages/ExpiredLinkPage/ExpiredLinkPage";
import { StepHeader } from "./StepHeader";
import { StepFields } from "./StepFields";
import { OnboardingCompleted } from "./OnboardingCompleted";
import { StepperActions } from "./StepperActions";
import { CustomStepIcon } from "./CustomStepIcon";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useOnboardingForm } from "./useOnboardingForm";
import { Overlay, OverlayContent, OverlayHeader } from "~/common/components/Overlay/Overlay";
import { BearignPattern } from "./BearingPattern";
import { useOnboardingDropdownValues } from "../../hooks/useOnboardingDropdownValues";
import type { LoginAfterRegistration } from "../../hooks/OnboardingOverlayContext";

interface Props {
  data: OnboardingFormData;
  loginAfterRegistration: LoginAfterRegistration;
  loginErrorComponent?: ReactNode;
  onSignUpClick: () => void;
}

const OnboardingOverlay = ({ data, loginAfterRegistration, loginErrorComponent, onSignUpClick }: Props) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;
  const dropdownOptions = useOnboardingDropdownValues(data);
  const FORM_WIDTH = isMobile ? "328px" : "480px";
  const steps = useMemo(() => createOnboardingSteps(dropdownOptions, t), [dropdownOptions, t]);

  const {
    formProps,
    activeStep,
    currentStep,
    isLastStep,
    completed,
    isLoginInFlight,
    isValidLink,
    globalErrors,
    isStepComplete,
    isSkippable,
    isExitDialogOpen,
    closeExitDialog,
    openExitDialog,
    handleBack,
    handleNext,
    onSubmitHandler,
    handleStartBrowsing,
    handleConfirmExit,
  } = useOnboardingForm({
    data,
    steps,
    languageOptions: dropdownOptions.languageOptions,
    loginAfterRegistration,
  });

  const showDeleteConfirmation = isExitDialogOpen && !completed && isValidLink;

  return (
    <FormProvider {...formProps}>
      <Overlay open={true}>
        <OverlayHeader onClose={completed ? handleStartBrowsing : isValidLink ? openExitDialog : handleConfirmExit} />

        <OverlayContent
          sx={{
            position: "relative",
            zIndex: 1,
            ":last-child": {
              pb: 0,
            },
          }}
        >
          {!isValidLink ? (
            <ExpiredLinkPage errors={globalErrors} onSignUpClick={onSignUpClick} />
          ) : (
            <form
              {...formProps}
              style={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <StepperContainer width={FORM_WIDTH}>
                <Stepper
                  activeStep={completed ? steps.length : activeStep}
                  alternativeLabel={false}
                  orientation="horizontal"
                  sx={{
                    "& .MuiStep-root": { px: "4px" },
                    "& .MuiStepLabel-iconContainer": { pr: 0 },
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={step.label} completed={completed || activeStep > index}>
                      <StepLabel
                        slots={{
                          stepIcon: (props) => <CustomStepIcon {...props} steps={steps} />,
                        }}
                      />{" "}
                    </Step>
                  ))}
                </Stepper>
              </StepperContainer>
              <FormContent width={FORM_WIDTH}>
                {loginErrorComponent}
                {!completed ? (
                  <>
                    <StepHeader title={currentStep.title} subtitle={currentStep.subtitle} />
                    <StepFields fields={currentStep.fields} />
                  </>
                ) : (
                  <OnboardingCompleted onStartBrowsing={handleStartBrowsing} loading={isLoginInFlight} />
                )}
                {!completed && (
                  <StepperActions
                    activeStep={activeStep}
                    isSkippable={isSkippable}
                    handleBack={handleBack}
                    handleNext={() => {
                      if (isLastStep) {
                        formProps.handleSubmit(onSubmitHandler)();
                      } else {
                        handleNext();
                      }
                    }}
                    canContinue={isStepComplete}
                  />
                )}
              </FormContent>
            </form>
          )}
        </OverlayContent>
        <BearignPattern />
      </Overlay>
      <Dialog
        open={showDeleteConfirmation}
        maxWidth="tablet"
        slotProps={{
          paper: {
            sx: {
              width: "312px",
              maxWidth: "312px",
            },
          },
        }}
      >
        <DialogHeader onClose={closeExitDialog}>
          <Typography variant="headlineSmall">{t("onboarding.overlay.exitDialog.title")}</Typography>
        </DialogHeader>
        <DialogContent>
          <Typography>{t("onboarding.overlay.exitDialog.subtitle")}</Typography>
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={handleConfirmExit}>
            {t("common.yes")}
          </Button>
          <Button variant="text" onClick={closeExitDialog}>
            {t("common.no")}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default OnboardingOverlay;

export const FormContent = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "width",
})<{ width: string }>(({ theme, width }) => ({
  margin: "72px auto 0",
  gap: "32px",
  width,
  maxWidth: width,
  [theme.breakpoints.only("phone")]: {
    margin: "24px auto 0",
  },
}));

export const StepperContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "width",
})<{ width: string }>(({ theme, width }) => ({
  width: "100%",
  maxWidth: width,
  margin: "56px auto 0",
  [theme.breakpoints.only("phone")]: {
    margin: "16px auto 0",
  },
}));
